/**
 * 새 프로젝트 생성 모달. cloud-api `POST /projects`.
 *
 * 흐름: name 입력 시 slug 자동 생성 (사용자가 손대기 전까지). organization
 * 선택은 props 로 받은 목록에서 (현재 선택된 조직 prefill).
 *
 * 생성 후 데스크톱 앱에서 "기존 프로젝트 가져오기" 로 이 프로젝트를
 * 끌어다 로컬 폴더 연결 가능 — admin 이 웹에서 미리 셋업 → 멤버는
 * 데스크톱에서 연결만 하면 되는 흐름.
 */
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  createProject,
  type CommitStrategy,
  type OrganizationWithRole,
  type Project,
} from "@/lib/console-api";
import { extractErrorMessage } from "@/lib/api-client";

const SLUG_REGEX = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;

const COMMIT_STRATEGIES: { value: CommitStrategy; label: string; help: string }[] = [
  {
    value: "PER_PLAN_MULTI_COMMIT",
    label: "PER_PLAN_MULTI_COMMIT (기본)",
    help: "PLAN 당 PR 1개. 자식 태스크는 plan 브랜치에 커밋만 추가.",
  },
  {
    value: "PER_TASK_PR",
    label: "PER_TASK_PR",
    help: "DEVELOP 당 PR 1개. 통합 단계 없음.",
  },
  {
    value: "PER_PLAN_SINGLE_COMMIT",
    label: "PER_PLAN_SINGLE_COMMIT",
    help: "PLAN 당 단일 커밋. 자식들이 amend + force push.",
  },
];

function toSlug(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^a-z0-9-\s]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .slice(0, 60);
}

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (project: Project) => void;
  organizations: OrganizationWithRole[];
  defaultOrganizationId?: string;
}

export function CreateProjectDialog({
  open,
  onClose,
  onCreated,
  organizations,
  defaultOrganizationId,
}: Props): JSX.Element | null {
  const [organizationId, setOrganizationId] = useState(
    defaultOrganizationId ?? organizations[0]?.id ?? "",
  );
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [gitUrl, setGitUrl] = useState("");
  const [defaultBranch, setDefaultBranch] = useState("main");
  const [commitStrategy, setCommitStrategy] =
    useState<CommitStrategy>("PER_PLAN_MULTI_COMMIT");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    nameRef.current?.focus();
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) {
      setName("");
      setSlug("");
      setSlugManuallyEdited(false);
      setGitUrl("");
      setDefaultBranch("main");
      setCommitStrategy("PER_PLAN_MULTI_COMMIT");
      setError(null);
      setSubmitting(false);
    } else if (defaultOrganizationId) {
      setOrganizationId(defaultOrganizationId);
    }
  }, [open, defaultOrganizationId]);

  if (!open) return null;

  const onSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (!organizationId) {
      setError("조직을 선택해 주세요");
      return;
    }
    if (!SLUG_REGEX.test(slug)) {
      setError("slug 은 소문자/숫자/하이픈만 가능합니다 (예: my-project)");
      return;
    }
    setSubmitting(true);
    try {
      const created = await createProject({
        organizationId,
        name,
        slug,
        gitUrl: gitUrl.trim() || undefined,
        defaultBranch: defaultBranch.trim() || "main",
        commitStrategy,
      });
      onCreated(created);
      onClose();
    } catch (err) {
      setError(extractErrorMessage(err, "프로젝트 생성에 실패했습니다"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-lg rounded-lg border border-border bg-card p-6 shadow-lg">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold">새 프로젝트</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              데스크톱 앱에서 이 프로젝트를 골라 로컬 폴더에 연결하면 작업이 시작됩니다.
            </p>
          </div>
          <button
            type="button"
            aria-label="닫기"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label htmlFor="proj-org" className="text-sm font-medium">
              조직
            </label>
            <select
              id="proj-org"
              required
              value={organizationId}
              onChange={(e) => setOrganizationId(e.target.value)}
              className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {organizations.length === 0 && (
                <option value="">조직을 먼저 만들어 주세요</option>
              )}
              {organizations.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name} ({o.role})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="proj-name" className="text-sm font-medium">
              프로젝트 이름
            </label>
            <Input
              id="proj-name"
              ref={nameRef}
              type="text"
              required
              maxLength={120}
              className="mt-1.5"
              placeholder="예: Devit Web"
              value={name}
              onChange={(e) => {
                const v = e.target.value;
                setName(v);
                if (!slugManuallyEdited) setSlug(toSlug(v));
              }}
            />
          </div>

          <div>
            <label htmlFor="proj-slug" className="text-sm font-medium">
              Slug{" "}
              <span className="text-muted-foreground">
                (URL · 소문자/숫자/하이픈)
              </span>
            </label>
            <Input
              id="proj-slug"
              type="text"
              required
              minLength={1}
              maxLength={60}
              className="mt-1.5 font-mono"
              placeholder="devit-web"
              value={slug}
              onChange={(e) => {
                setSlug(e.target.value);
                setSlugManuallyEdited(true);
              }}
            />
          </div>

          <div>
            <label htmlFor="proj-git" className="text-sm font-medium">
              Git URL <span className="text-muted-foreground">(선택)</span>
            </label>
            <Input
              id="proj-git"
              type="text"
              maxLength={500}
              className="mt-1.5"
              placeholder="https://github.com/team/repo"
              value={gitUrl}
              onChange={(e) => setGitUrl(e.target.value)}
            />
          </div>

          <div>
            <label htmlFor="proj-branch" className="text-sm font-medium">
              기본 브랜치
            </label>
            <Input
              id="proj-branch"
              type="text"
              maxLength={200}
              className="mt-1.5 font-mono"
              placeholder="main"
              value={defaultBranch}
              onChange={(e) => setDefaultBranch(e.target.value)}
            />
          </div>

          <div>
            <label htmlFor="proj-strategy" className="text-sm font-medium">
              커밋 전략
            </label>
            <select
              id="proj-strategy"
              value={commitStrategy}
              onChange={(e) => setCommitStrategy(e.target.value as CommitStrategy)}
              className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {COMMIT_STRATEGIES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-muted-foreground">
              {COMMIT_STRATEGIES.find((s) => s.value === commitStrategy)?.help}
            </p>
          </div>

          {error && (
            <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={submitting}
            >
              취소
            </Button>
            <Button type="submit" disabled={submitting || !organizationId}>
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              만들기
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
